import { useEffect, useMemo, useState } from "react";
import {
    UiDialogBody,
    UiDialogClose,
    UiDialogContent,
    UiDialogDescription,
    UiDialogFooter,
    UiDialogHeader,
    UiDialogRoot,
    UiDialogTitle,
} from "../../../components/ui/ant/Dialog";
import {
    Form,
    Input,
    Select
} from 'antd';
import { ContentApi } from "../../../shared/api/contentApi";

const INITIAL_FORM_STATE = {
    systemName: "",
    name: "",
    comment: "",
};

export default function CreateVersionDialog({ open, onClose, projectId, versions = [], onCreated }) {
    const [form, setForm] = useState(INITIAL_FORM_STATE);
    const [parentVersionId, setParentVersionId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Оптимизированная фильтрация
    const publishedVersions = useMemo(() => {
        return versions.filter((v) => v?.status?.toUpperCase() === "PUBLISHED");
    }, [versions]);

    const sourceVersions = publishedVersions.length > 0 ? publishedVersions : versions;

    // Сброс формы при открытии/закрытии диалога
    useEffect(() => {
        if (!open) return;
        
        setError("");
        setSaving(false);
        setForm(INITIAL_FORM_STATE);
        
        // Безопасная установка дефолтного ID без зацикливания
        const firstVersionId = sourceVersions[0]?.id;
        setParentVersionId(firstVersionId ? String(firstVersionId) : "");
    }, [open, sourceVersions]); // eslint-disable-line react-hooks/exhaustive-deps

    const canSubmit = !!projectId && !!parentVersionId && !saving;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault(); // Предотвращаем перезагрузку страницы
        if (!canSubmit) return;

        setSaving(true);
        setError("");
        
        try {
            const payload = {
                sourceVersionId: parentVersionId,
                name: form.name.trim(),
                comment: form.comment.trim() || null,
                systemName: form.systemName.trim()
            };
            
            const created = await ContentApi.forkVersion(projectId, payload);
            
            onCreated?.(created);
            onClose?.();
        } catch (e) {
            setError(e?.message || "Не удалось создать версию");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <UiDialogRoot open={open} onOpenChange={(next) => !next && onClose?.()}>
            <UiDialogContent size="md">
                {/* Оборачиваем весь контент в form, чтобы кнопка submit в футере работала */}
                <Form layout='vertical' onSubmit={handleSubmit} className="flex flex-col h-full">
                    <UiDialogHeader>
                        <div>
                            <UiDialogTitle>Создать версию</UiDialogTitle>
                            <UiDialogDescription>
                                Создание draft-версии через fork от существующей версии.
                            </UiDialogDescription>
                        </div>
                        <UiDialogClose showIcon />
                    </UiDialogHeader>

                    <UiDialogBody>
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                                {error}
                            </div>
                        )}

                        {sourceVersions.length === 0 ? (
                            <div className="space-y-2">
                                <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-3 text-sm text-zinc-500">
                                    Нет версий-источников для создания новой версии.
                                </div>
                                <p className="text-xs text-amber-600">
                                    Для создания версии необходимо сначала создать хотя бы одну версию.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <label className="eyebrow">Версия-источник</label>
                                <Select
                                    value={parentVersionId}
                                    onChange={setParentVersionId}
                                    placeholder="Выберите версию"
                                    className="w-full"
                                    options={sourceVersions.map((version) => ({
                                        value: String(version.id),
                                        label: `${version.name || version.systemName || `#${version.id}`} (${version.status || "UNKNOWN"})`,
                                    }))}
                                />
                                {publishedVersions.length === 0 && (
                                    <p className="text-xs text-amber-600">
                                        Published версий нет, используется любая доступная версия.
                                    </p>
                                )}
                                <Form.Item label="System Name *">
                                    <Input
                                        name="systemName"
                                        className="input min-w-full"
                                        value={form.systemName}
                                        onChange={handleInputChange}
                                        placeholder="version_"
                                        required
                                    />
                                </Form.Item>
                                
                                <Form.Item label="Название *">
                                    <Input
                                        name="name"
                                        className="input min-w-full"
                                        value={form.name}
                                        onChange={handleInputChange}
                                        placeholder="Версия"
                                        required
                                    />
                                </Form.Item>
                                
                                <Form.Item label="Комментарий">
                                    <Input.TextArea
                                        name="comment"
                                        className="input min-w-full"
                                        rows={3}
                                        value={form.comment}
                                        onChange={handleInputChange}
                                        placeholder="Комментарий"
                                    />
                                </Form.Item>
                            </div>
                        )}
                    </UiDialogBody>

                    <UiDialogFooter>
                        <button type="button" className="btn btn-outline" onClick={onClose}>
                            Отмена
                        </button>
                        <button
                            type="submit"
                            className="btn btn-soft-accent"
                            disabled={!canSubmit || sourceVersions.length === 0}
                        >
                            {saving ? "Создаем..." : "Создать версию"}
                        </button>
                    </UiDialogFooter>
                </Form>
            </UiDialogContent>
        </UiDialogRoot>
    );
}